import { noop } from 'underscore';
import React, { useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import {
  getIsImportFlowEnabled, uetReportConversion, gTagReportConversion,
} from '@bingads-webui-campaign-react/pmax-landing-utils';
import { getIsInputState, getIsInitState } from './use-dynamic-flow-state';
import { getDiscoverSectionStyles } from '../styles/discover-section-styles';
import { WebsitePromptInputs } from '../shared';
import { CampaignCreationMethodSection } from './campaign-creation-method-section';

export function DiscoverSection({
  currentState,
  triggerTransition,
  onClickSignUp,
  website,
  setWebsite,
  websiteInputRef,
  methodsContainerRef,
  logger,
}) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getDiscoverSectionStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const isInputState = getIsInputState(currentState);
  const isInitState = getIsInitState(currentState);
  const isImportFlowEnabled = getIsImportFlowEnabled();

  const [isValid, setIsValid] = useState(true);
  const [alwaysShowError, setAlwaysShowError] = useState(false);

  const onClickCreateNow = useCallback(() => {
    uetReportConversion();
    gTagReportConversion();
    if (isValid) {
      logger.trace(`[DiscoverSection] Create now clicked to submit website. Website: ${website}`);
      triggerTransition();
    } else {
      logger.trace(`[DiscoverSection] Create now clicked with invalid website. Website: ${website}`);
      setAlwaysShowError(true);
    }
  }, [isValid, logger, triggerTransition, website]);

  useEffect(() => {
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      let combinedText = `${i18n.getString(_TL_("Discover what's possible"))} ${i18n.getString(_TL_('with Microsoft Advertising'))},`;
      if (isImportFlowEnabled) {
        combinedText += ` ${i18n.getString(_TL_('Explore how you can connect with millions of searchers not using Google.'))}`;
      } else {
        combinedText += ` ${i18n.getString(_TL_('Enter your website url to see how your ads will look like across Microsoft Advertising Networks'))}`;
      }

      meta.setAttribute('content', combinedText);
    }
  }, [i18n, isImportFlowEnabled]);

  return (
    <div
      className={classNames(
        'discover-content',
        classes.contentContainer,
        classes[`contentContainer-${displayMode}`]
      )}
    >
      <div
        className={classNames(
          'discover-slogan',
          classes.sloganContainer,
          classes[`sloganContainer-${displayMode}`]
        )}
      >
        <h1 className={classNames('discover-title', classes.discoverTitle, classes[`discoverTitle-${displayMode}`])}>
          {i18n.getString(_TL_("Discover what's possible"))}
        </h1>
        <h2 className={classNames('discover-subtitle', classes.discoverSubtitle, classes[`discoverSubtitle-${displayMode}`])}>
          {i18n.getString(_TL_('with Microsoft Advertising'))}
        </h2>
        <p className={classNames('discover-description', classes.discoverDescription, classes[`discoverDescription-${displayMode}`])}>
          {isImportFlowEnabled
            ? i18n.getString(_TL_('Explore how you can connect with millions of searchers not using Google. '))
            : i18n.getString(_TL_('Enter your website url to see how your ads will look like across Microsoft Advertising Networks'))
          }
        </p>
        {isImportFlowEnabled && isInputState && (
          <CampaignCreationMethodSection
            website={website}
            setWebsite={setWebsite}
            websiteInputRef={websiteInputRef}
            methodsContainerRef={methodsContainerRef}
            setIsValid={setIsValid}
            alwaysShowError={alwaysShowError}
            displayMode={displayMode}
            onClickCreatePreviewAds={onClickCreateNow}
            onClickStartImportGoogle={onClickSignUp}
            onClickStartImportFacebook={onClickSignUp}
            logger={logger}
          />
        )}
        {!isImportFlowEnabled && isInputState && (
          <div
            className={classNames(
              'discover-inputs-box',
              classes.inputsBoxContainer,
              classes[`inputsBoxContainer-${displayMode}`]
            )}
          >
            <WebsitePromptInputs
              websiteInputRef={websiteInputRef}
              website={website}
              setWebsite={setWebsite}
              setIsValid={setIsValid}
              alwaysShowError={alwaysShowError}
              showPrompt={false}
            />
          </div>
        )}
        {(!isImportFlowEnabled || isInitState) &&
          <Button
            className="create-now-button"
            onClick={onClickCreateNow}
            appearance="primary"
          >
            {i18n.getString(_TL_('Try it now'))}
          </Button>}
      </div>
    </div>
  );
}

DiscoverSection.propTypes = {
  currentState: PropTypes.string.isRequired,
  triggerTransition: PropTypes.func.isRequired,
  onClickSignUp: PropTypes.func.isRequired,
  website: PropTypes.string.isRequired,
  setWebsite: PropTypes.func.isRequired,
  websiteInputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  methodsContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

DiscoverSection.defaultProps = {
  websiteInputRef: { current: null },
  methodsContainerRef: { current: null },
  logger: {
    trace: noop,
  },
};
